import type { InstallPromptId } from './types';

import { safeLocalStorage } from '@/lib/safe-storage';
import { STORAGE_KEYS } from '@/lib/storage-keys';

/** Activity counted per calendar day, which decides when the day's moments arrive. */
export type DailyCounter = 'inboxVisits' | 'reviews';

export type InstallPromptHistory = {
  /** When this browser first counted anything; nobody is prompted on their first day. */
  firstSeenAt: number;
  lastShownAt: number | null;
  dismissed: InstallPromptId[];
  /** Local date (YYYY-MM-DD) the counters belong to. */
  day: string;
  counters: Record<DailyCounter, number>;
};

const DAY_MS = 24 * 60 * 60 * 1000;
/** At most one prompt a week, across the desktop app and Slack. */
export const PROMPT_INTERVAL_MS = 7 * DAY_MS;
/** Nobody is prompted within a day of first using cubic in this browser. */
export const FIRST_DAY_MS = DAY_MS;
/** Two dismissals mean the person does not want these; stop offering any. */
export const MAX_DISMISSALS = 2;

function localDay(now: number): string {
  const date = new Date(now);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function freshHistory(now: number): InstallPromptHistory {
  return {
    firstSeenAt: now,
    lastShownAt: null,
    dismissed: [],
    day: localDay(now),
    counters: { inboxVisits: 0, reviews: 0 },
  };
}

function isCount(value: unknown): boolean {
  return Number.isInteger(value) && (value as number) >= 0;
}

function isHistory(value: unknown): value is InstallPromptHistory {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const history = value as Partial<InstallPromptHistory>;
  const counters = history.counters as Partial<Record<DailyCounter, unknown>> | null | undefined;
  return (
    Number.isFinite(history.firstSeenAt) &&
    (history.lastShownAt === null || Number.isFinite(history.lastShownAt)) &&
    Array.isArray(history.dismissed) &&
    history.dismissed.every((entry: unknown) => typeof entry === 'string') &&
    typeof history.day === 'string' &&
    typeof counters === 'object' &&
    counters !== null &&
    isCount(counters.inboxVisits) &&
    isCount(counters.reviews)
  );
}

/** A new day starts the day's counters from zero. */
function forDay(history: InstallPromptHistory, now: number): InstallPromptHistory {
  const day = localDay(now);
  return history.day === day
    ? history
    : { ...history, day, counters: { inboxVisits: 0, reviews: 0 } };
}

export function readHistory(now: number): InstallPromptHistory {
  let stored: unknown = null;
  try {
    stored = JSON.parse(safeLocalStorage.getItem(STORAGE_KEYS.INSTALL_PROMPTS) ?? 'null');
  } catch {
    stored = null;
  }
  return forDay(isHistory(stored) ? stored : freshHistory(now), now);
}

function writeHistory(history: InstallPromptHistory): void {
  safeLocalStorage.setItem(STORAGE_KEYS.INSTALL_PROMPTS, JSON.stringify(history));
}

/** Counts one more of today's `counter` and returns today's total. */
export function countToday(counter: DailyCounter, now = Date.now()): number {
  const history = readHistory(now);
  const count = history.counters[counter] + 1;
  writeHistory({ ...history, counters: { ...history.counters, [counter]: count } });
  return count;
}

export function readToday(counter: DailyCounter, now = Date.now()): number {
  return readHistory(now).counters[counter];
}

/** After this many dismissals the person does not want app promotions at all. */
export function hasOptedOut(history: InstallPromptHistory): boolean {
  return history.dismissed.length >= MAX_DISMISSALS;
}

/** Whether a prompt, or the first-review desktop toast, already used this week's slot. */
export function wasShownThisWeek(history: InstallPromptHistory, now: number): boolean {
  return history.lastShownAt !== null && now - history.lastShownAt < PROMPT_INTERVAL_MS;
}

/** Whether `id` may be shown now: past the first day, a week after the last prompt, never dismissed. */
export function canShowPrompt(
  history: InstallPromptHistory,
  id: InstallPromptId,
  now: number,
): boolean {
  return (
    now - history.firstSeenAt >= FIRST_DAY_MS &&
    !wasShownThisWeek(history, now) &&
    !hasOptedOut(history) &&
    !history.dismissed.includes(id)
  );
}

export function recordShown(now = Date.now()): void {
  writeHistory({ ...readHistory(now), lastShownAt: now });
}

/** "Not now" retires this moment for good. */
export function recordDismissed(id: InstallPromptId, now = Date.now()): void {
  const history = readHistory(now);
  if (!history.dismissed.includes(id)) {
    writeHistory({ ...history, dismissed: [...history.dismissed, id] });
  }
}

/** Saves the first-seen time on a first visit, so the first-day rule has a start. */
export function ensureHistory(now = Date.now()): void {
  if (safeLocalStorage.getItem(STORAGE_KEYS.INSTALL_PROMPTS) === null) {
    writeHistory(freshHistory(now));
  }
}
